import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ConfirmDialogProvider } from './contexts/ConfirmDialogContext';
import { Layout } from './components/Layout';
import { Spinner } from './components/Spinner';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { WorkshopsPage } from './pages/WorkshopsPage';
import { ReservationsPage } from './pages/ReservationsPage';
import { CustomOrdersPage } from './pages/CustomOrdersPage';
import { PaintKitsPage } from './pages/PaintKitsPage';
import { PaintKitOrdersPage } from './pages/PaintKitOrdersPage';
import { ColorPalettesPage } from './pages/ColorPalettesPage';
import { ColorPaletteOrdersPage } from './pages/ColorPaletteOrdersPage';
import { CategoriesPage } from './pages/CategoriesPage';
import { CollectionsPage } from './pages/CollectionsPage';
import { PaymentSettingsPage } from './pages/PaymentSettingsPage';
import { PromotionPage } from './pages/PromotionPage';
import { AnnouncementPage } from './pages/AnnouncementPage';
import { ReportsPage } from './pages/ReportsPage';
import './index.css';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { admin, loading } = useAuth();

  if (loading) {
    return <Spinner size="lg" fullPage />;
  }

  if (!admin) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { admin, loading } = useAuth();

  if (loading) {
    return <Spinner size="lg" fullPage />;
  }

  if (admin) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <ConfirmDialogProvider>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/workshops"
            element={
              <ProtectedRoute>
                <WorkshopsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reservations"
            element={
              <ProtectedRoute>
                <ReservationsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/custom-orders"
            element={
              <ProtectedRoute>
                <CustomOrdersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/paint-kits"
            element={
              <ProtectedRoute>
                <PaintKitsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/paint-kit-orders"
            element={
              <ProtectedRoute>
                <PaintKitOrdersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/color-palettes"
            element={
              <ProtectedRoute>
                <ColorPalettesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/color-palette-orders"
            element={
              <ProtectedRoute>
                <ColorPaletteOrdersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/categories"
            element={
              <ProtectedRoute>
                <CategoriesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/collections"
            element={
              <ProtectedRoute>
                <CollectionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payment-settings"
            element={
              <ProtectedRoute>
                <PaymentSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/promotion"
            element={
              <ProtectedRoute>
                <PromotionPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/announcement"
            element={
              <ProtectedRoute>
                <AnnouncementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports"
            element={
              <ProtectedRoute>
                <ReportsPage />
              </ProtectedRoute>
            }
          />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </ConfirmDialogProvider>
  );
}

export default App;
